import * as React from "react";
import {useEffect, useRef} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import Container from "@mui/material/Container";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {IconButton, InputAdornment, Link} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import {useAppDispatch, useAppSelector} from "../../redux/store";
import {userLogin, verifyMfaOtp} from "../../redux/reducers/authAction";
import {Loader} from "components/FormComponents/Loader/Loader";
import {loginStyle} from "./loginStyle";
import CPSLogo from "../../assets/cps_logo_white.webp";
import LillyLogo from "../../assets/lilly-logo.webp";
import PrivacyChoiceLogo from "../../assets/privacyoptions.1cfe240.svg";
import {FormTextField} from "components/FormComponents/TextField/FormTextField";
import LoginFormInputLabel from "./LoginFormInputLabel";
import {FormButton} from "components/FormComponents/Button/FormButton";
import FormLink from "components/FormComponents/Link/FormLink";
import FormParagraph from "components/FormComponents/Paragraph/FormParagraph";
import {clearlogs, postLogsToAPI} from "../../redux/reducers/loggerSlice";
import type {menu} from "../../redux/reducers/authSlice";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {encrypt} from "commonUtils/crypto";
import {HttpException} from "services/exceptionHandlerService";
import {
  HttpStatusCode,
  CPSConstants,
  CPSMessages,
  AxiosErrorCode,
  Alerts,
  PageNames,
  LoginType,
  CPSMessageCodes,
} from "commonUtils/enumerations";
import LoginBackGroundImage from "../../assets/login_background_image.webp";
import {logMessage} from "services/loggerService";
import {isValidObject} from "commonUtils/utils";
import {OLD_CPS_URL, LOGIN_RESEND_OTP} from "commonUtils/globalAPISettings";
import DOMPurify from "dompurify";
import OTPComp from "components/OTP/OTPComp";
import {postRequest, setHeader} from "repository/genericRepository";

interface LoginContentProps {
  privacy: string;
  termsOfUse: string;
  lillyLogin: string;
  usAccessibility: string;
  caAccessibility: string;
  euAccessibility: string;
  chpNotice: string;
  cookiePrivacy: string;
  cookieSetting: string;
  cpsLoginMessage: string;
  maintenanceMessage: string;
}

interface contentProps {
  errorContentMsg: string;
  contentProps: LoginContentProps;
}
const LoginUI: React.FC<contentProps> = ({errorContentMsg, contentProps}) => {
  const [searchParams] = useSearchParams();
  const logoutStatus = searchParams.get("logoutcase");
  const searchParamsRedirect = searchParams.get("RedirectUrl");
  const logoutStatusRedirect = searchParamsRedirect?.includes(".aspx") // TODO: Cleanup OLD
    ? "1"
    : "";
  // const appLogoutStatus = searchParams.get("AppLogout");
  const inputRef = useRef<HTMLInputElement>();
  const textInput = useRef<HTMLInputElement>();
  const otpCompRef = useRef(null);
  const btnVerifyRef = useRef(null);

  const APIHeaders = {
    Authorization: `Bearer ${process.env.REACT_APP_API_AUTH_TOKEN}`,
  };
  const dispatch = useAppDispatch();
  const logs = useAppSelector(state => {
    return state.logs;
  });
  const {authDetails, userDetails, userPreferences} = useAppSelector(state => {
    return state.auth;
  });
  const [loader, setLoader] = React.useState<boolean>(false);
  const [user, setuserIdError] = React.useState<boolean>(false);
  const [password, setPasswordError] = React.useState<boolean>(false);
  const [icon, showIcon] = React.useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string>(errorContentMsg);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [mfaLoginDetail, setMfaLoginDetail] = React.useState<any>();
  const [showOTP, setShowOTP] = React.useState<boolean>(true);
  const [isMfaRequired, setIsMfaRequired] = React.useState<boolean>(false);

  const handleClickShowPassword = () => {
    try {
      setErrorMsg("");
      setShowPassword(!showPassword);
    } catch (error: any) {
      setLoader(false);
      setErrorMsg(error.message);
    }
  };
  const navigate = useNavigate();
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      setErrorMsg("");
      logMessage("INFO", "User has clicked the login button.", PageNames.LOGIN);
      const data = new FormData(event.currentTarget);
      const loginDetails = {
        userIdentity: data.get("userid"),
        password: data.get("password"),
      };
      if (!loginDetails.userIdentity && !loginDetails.password) {
        setuserIdError(true);
        setPasswordError(true);
        logMessage("ERROR", "User and password empty.", PageNames.LOGIN);
      } else if (!loginDetails.userIdentity) {
        logMessage("ERROR", "User name empty.", PageNames.LOGIN);
        setuserIdError(true);
      } else if (!loginDetails.password) {
        logMessage("ERROR", "User password empty.", PageNames.LOGIN);
        setPasswordError(true);
      } else {
        setuserIdError(false);
        setPasswordError(false);
        setLoader(true);
        dispatch(
          userLogin({
            userIdentity: loginDetails.userIdentity,
            password: loginDetails.password,
            isEncrypted: false,
            loginTypeId: LoginType.MFA,
          }),
        )
          .unwrap()
          .then(responseData => {
            if (responseData?.isMfaRequired) {
              setIsMfaRequired(true);
              setMfaLoginDetail(responseData);
              setShowOTP(true);
            } else {
              // use Effect will handel login
            }
            setLoader(false);
          })
          .catch(error => {
            setLoader(false);
            handleAxiosErrors(error);
          });
      }
    } catch (error: any) {
      setLoader(false);
      handleAxiosErrors(error);
    }
  };
  useEffect(() => {
    const userIdTextField = document.getElementById("userid");
    userIdTextField?.focus();
    showIcon(true);
  }, []);
  useEffect(() => {
    try {
      logMessage("INFO", "User has loaded the login page.", PageNames.LOGIN);
      setLoader(false);
      if (authDetails.isAuthenticated) {
        logMessage("INFO", "Login successfull.", PageNames.LOGIN);
        dispatch(postLogsToAPI({logs, token: authDetails?.token}));
        dispatch(clearlogs());
        if (userDetails?.isMultipleProfiles) {
          navigate("/userprofile");
        } else if (userDetails?.isChangePassword) {
          window.location.href = "/changepwd";
        } else if (userDetails?.isFirstTime) {
          window.location.href = "/firstlogin";
        } else {
          let isNew = true;
          const menus: menu[] = userPreferences?.menus?.filter(function (
            id: menu,
          ) {
            return id.pageToLoad === userDetails?.pageToLoad;
          });
          if (isValidObject(menus) && menus.length > 0) {
            isNew = menus[0].isNew;
          }
          if (isNew) {
            navigate("/" + userDetails?.pageToLoad);
          } else {
            setLoader(true);
            const userIdentity = encrypt(userDetails?.userIdentity);
            const userId = encrypt(userDetails?.userId?.toString());
            const profileId = encrypt(userDetails?.profileId?.toString());
            const switchedUserId = encrypt(
              userDetails?.switchedUserId?.toString(),
            );
            const switchedProfileId = encrypt(
              userDetails?.switchedProfileId?.toString(),
            );
            const switchedUserName = encrypt(
              userDetails?.switchedUserName?.toString(),
            );
            const token = authDetails?.token;
            const oldLink = `${OLD_CPS_URL}${userDetails?.pageToLoad}?userIdentity=${userIdentity}&userId=${userId}&profileId=${profileId}&switchedUserId=${switchedUserId}&switchedProfileId=${switchedProfileId}&switchedUserName=${switchedUserName}&token=${token}`;
            window.location.href = oldLink;
          }
        }
      } else {
        // To show error mgs from loginmaintance page. on first load icon will be false. only after first load should show user details message.
        if (icon) {
          setErrorMsg(userDetails?.message);
        }
        textInput.current && (textInput.current.value = "");
      }
    } catch (error: any) {
      setLoader(false);
      setErrorMsg(userDetails?.message);
      textInput.current && (textInput.current.value = "");
    }
  }, [authDetails, userDetails, userPreferences]); // TODO: have to check this dependency

  const handleBackToLogin = () => {
    setErrorMsg("");
    setShowOTP(false);
    setIsMfaRequired(false);
  };

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.LOGIN};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        if (
          error.response.data.messageCode ===
          CPSMessageCodes.ACCOUNT_LOCKED_DUE_TO_FAILED_ATTEMPTS
        ) {
          setIsMfaRequired(false);
        }
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_BAD_RESPONSE) {
      setErrorMsg(CPSMessages.CONTACT_SUPPORT);
    } else {
      setErrorMsg(error.message);
    }
  };

  const handleVerifyMfaOtp = () => {
    setErrorMsg("");
    logMessage("INFO", "User has clicked the verify button.", PageNames.LOGIN);

    let otp = null;
    if (otpCompRef?.current) {
      const current: any = otpCompRef?.current;
      otp = current?.verify();
    }
    if (otp && otp.length === 6) {
      try {
        setLoader(true);
        dispatch(
          verifyMfaOtp({
            userIdentity: userDetails?.userIdentity,
            otp,
          }),
        )
          .unwrap()
          .then(response => {
            // use effect should handle state changes set error if not authenticated.
            if (!response?.isAuthenticated) {
              setErrorMsg(response?.message);
            }
            setLoader(false);
          })
          .catch(error => {
            setLoader(false);
            handleAxiosErrors(error);
          });
      } catch (error: any) {
        setLoader(false);
        handleAxiosErrors(error);
      }
    } else {
      setErrorMsg(CPSMessages.PLEASE_FILL_OTP);
    }
  };

  const handleResend = (e: any, otpSentMethod: number) => {
    e.preventDefault();
    setErrorMsg("");
    setLoader(true);
    setShowOTP(false);

    postRequest({
      url: LOGIN_RESEND_OTP,
      headers: setHeader(APIHeaders),
      params: {
        userIdentity: userDetails?.userIdentity,
        otpSentMethod,
      },
      timeout: 60000,
      data: {},
    })
      .then((responseData: any) => {
        if (responseData?.isMfaRequired) {
          setMfaLoginDetail(responseData);
          setShowOTP(true);
        } else if (responseData.loginTypeId === LoginType.Application) {
          //changed in MFA required ask user to login with username, password again.
          setIsMfaRequired(false);
        }
        if (responseData.message) {
          setErrorMsg(responseData.message);
        }
        setLoader(false);
      })
      .catch((error: any) => {
        setLoader(false);
        handleAxiosErrors(error);
      });
  };

  const setFocusToVerify = () => {
    if (btnVerifyRef?.current) {
      const btnVerify: any = btnVerifyRef?.current;
      btnVerify?.focusButton();
    }
  };

  return (
    <Box
      style={{
        backgroundImage: `url(${LoginBackGroundImage})`,
      }}
      id="login-form-image-container"
      sx={loginStyle.backgroundImageContainer}>
      <>
        <Container
          className="login-layout"
          sx={loginStyle.userProfileContainer}>
          <Box sx={loginStyle.loginImageBackgroundColor}>
            <img
              src={CPSLogo}
              alt="CPS Logo"
              style={loginStyle.userProfileStyleCpsLogo}
            />
          </Box>
          <Box>
            <Loader open={loader} />
            {!isMfaRequired && (
              <>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid
                    container
                    sx={loginStyle.loginGridContainer}
                    spacing={2}>
                    {(logoutStatusRedirect === "1" ||
                      logoutStatus === "1" ||
                      logoutStatus === " 1") &&
                      !errorMsg && (
                        <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                          <FormValidationAlert
                            severity={Alerts.SUCCESS}
                            message={CPSMessages.LOGOUT_SUCCESSFULLY}
                          />
                        </Grid>
                      )}
                    {(logoutStatus === "2" || logoutStatus === " 2") &&
                      !errorMsg && (
                        <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                          <FormValidationAlert
                            severity={Alerts.ERROR}
                            message={CPSMessages.LOGOUT_ANOTHER_SYSTEM}
                          />
                        </Grid>
                      )}
                    {(logoutStatus === "3" || logoutStatus === " 3") &&
                      !errorMsg && (
                        <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                          <FormValidationAlert
                            severity={Alerts.SUCCESS}
                            message={CPSMessages.SESSION_EXPIRED}
                          />
                        </Grid>
                      )}
                    {errorMsg && (
                      <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                        <FormValidationAlert
                          severity={Alerts.ERROR}
                          message={errorMsg?.split(".")?.join(".<br/>")}
                        />
                      </Grid>
                    )}
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <Stack spacing={CPSConstants.MUI_STACK_LOGINFORM_SPACE}>
                        <LoginFormInputLabel text={"User ID"} />
                        <FormTextField
                          required
                          id="userid"
                          label=""
                          name="userid"
                          autoComplete="userid"
                          autoFocus
                          inputRef={inputRef}
                          helperText={
                            user ? CPSMessages.PLEASE_ENTER_USER_ID : ""
                          }
                          error={user}
                          onChange={event => {
                            setuserIdError(false);
                            const value = event.target.value;
                            event.target.value = value.trim();
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <Stack spacing={CPSConstants.MUI_STACK_LOGINFORM_SPACE}>
                        <LoginFormInputLabel text={"Password"} />
                        <FormTextField
                          fullWidth
                          required
                          name="password"
                          label=""
                          type={showPassword ? "text" : "password"}
                          id="password"
                          inputRef={textInput}
                          onCut={e => {
                            e.preventDefault();
                          }}
                          onCopy={e => {
                            e.preventDefault();
                          }}
                          onPaste={e => {
                            e.preventDefault();
                          }}
                          autoComplete="current-password"
                          helperText={
                            password ? CPSMessages.PLEASE_ENTER_PASSWORD : ""
                          }
                          error={password}
                          inputProps={{
                            maxLength: CPSConstants.MAX_LENGTH_TWENTY_FIVE,
                          }}
                          onChange={event => {
                            setPasswordError(false);
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {icon ? (
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    edge="end">
                                    {showPassword ? (
                                      <Visibility fontSize="small" />
                                    ) : (
                                      <VisibilityOff fontSize="small" />
                                    )}
                                  </IconButton>
                                ) : (
                                  ""
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <FormButton
                        fullWidth
                        type="submit"
                        variant="contained"
                        outlet={"Login"}
                      />
                    </Grid>
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <Container sx={loginStyle.loginThreeLinkStyle}>
                        <FormLink href="/changepwd" outlet="Change Password" />
                        <FormLink href="/forgotpwd" outlet="Forgot Password?" />
                        <FormLink
                          href="/requestaccount"
                          outlet="Request Account"
                        />
                      </Container>
                    </Grid>
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <FormParagraph
                        outlet={
                          <>
                            {contentProps?.cpsLoginMessage && (
                              <Box
                                sx={loginStyle.logincontentbox}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    contentProps?.cpsLoginMessage,
                                  ),
                                }}
                              />
                            )}
                            {contentProps?.maintenanceMessage && (
                              <Box
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    contentProps?.maintenanceMessage,
                                  ),
                                }}
                              />
                            )}
                          </>
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      sx={loginStyle.loginTwoLinkStyle}
                      xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <FormLink
                        href={contentProps?.termsOfUse}
                        target="_blank"
                        outlet="Terms of Use"
                      />
                      <Box sx={loginStyle.pipe} component="span">
                        &#124;
                      </Box>
                      <FormLink
                        href={contentProps?.privacy}
                        target="_blank"
                        outlet="Privacy Statement"
                      />
                      <Box sx={loginStyle.pipe} component="span">
                        &#124;
                      </Box>
                      <FormLink
                        href={contentProps?.usAccessibility}
                        target="_blank"
                        outlet="US Accessibility Statement"
                      />
                      {/* <Box sx={loginStyle.pipe} component="span">
                        &#124;
                      </Box>
                      <FormLink
                        href={contentProps?.caAccessibility}
                        target="_blank"
                        outlet="CA Accessibility Statement"
                      />
                      <Box sx={loginStyle.pipe} component="span">
                        &#124;
                      </Box>
                      <FormLink
                        href={contentProps?.euAccessibility}
                        target="_blank"
                        outlet="EU Accessibility Statement"
                      /> */}
                      <Box sx={loginStyle.pipe} component="span">
                        &#124;
                      </Box>
                      <FormLink
                        href={contentProps?.chpNotice}
                        target="_blank"
                        outlet="Consumer Health Privacy Notice"
                      />
                    </Grid>
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <Box component={"div"} display={"flex"}>
                        <img
                          src={PrivacyChoiceLogo}
                          alt="California Consumer Privacy Act (CCPA) Opt-Out Icon"
                          style={loginStyle.privacyChoiceLogo}
                        />
                        <Container sx={loginStyle.loginThreeLinkStyle}>
                          <FormLink
                            href={contentProps?.cookiePrivacy}
                            target="_blank"
                            outlet="Your Privacy Choices"
                          />
                          <FormLink
                            className="lds-link"
                            href={contentProps?.cookieSetting}
                            outlet="Cookie Settings"
                          />
                        </Container>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
            {isMfaRequired && (
              <Box>
                <Grid container sx={loginStyle.loginGridContainer} spacing={2}>
                  <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                    <Typography component="p" sx={loginStyle.headingtext}>
                      {"Multi Factor Authentication"}
                    </Typography>
                  </Grid>
                  {errorMsg && (
                    <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                      <FormValidationAlert
                        severity={Alerts.ERROR}
                        message={errorMsg?.split(".")?.join(".<br/>")}
                      />
                    </Grid>
                  )}
                  <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                    {showOTP && (
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            mfaLoginDetail?.otpSentTo ?? "",
                          ),
                        }}></Box>
                    )}
                  </Grid>

                  <Box sx={loginStyle.otpContainer}>
                    {showOTP && (
                      <OTPComp
                        ref={otpCompRef}
                        length={6}
                        handleOtpSent={(e: any, preferredMethod: number) => {
                          handleResend(e, preferredMethod);
                        }}
                        resendCountDown={mfaLoginDetail?.resendCountDown ?? 0}
                        lbltext="Did not receive code?"
                        otpSentMethod={mfaLoginDetail?.otpSentMethod}
                        handleFocusNext={() => {
                          setFocusToVerify();
                        }}
                      />
                    )}
                  </Box>

                  <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                    <Link
                      sx={loginStyle.formLink}
                      component="button"
                      onClick={(e: any) => {
                        handleResend(
                          e,
                          mfaLoginDetail?.otpSentMethod === 1 ? 2 : 1,
                        );
                      }}>
                      <Box>{mfaLoginDetail?.verifyMethodText}</Box>
                    </Link>

                    <FormButton
                      ref={btnVerifyRef}
                      fullWidth
                      onClick={handleVerifyMfaOtp}
                      disabled={!mfaLoginDetail?.isMfaRequired}
                      variant="contained"
                      outlet={"Verify"}
                    />
                  </Grid>
                  <Grid item xs={CPSConstants.MUI_TEXTFIELD_SIZE | 0}>
                    <Link
                      sx={loginStyle.formLink}
                      component="button"
                      onClick={handleBackToLogin}>
                      Back to Sign In
                    </Link>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Container>

        {!isMfaRequired && (
          <Link href={contentProps?.lillyLogin}>
            <Container
              className="login-layout-sso"
              sx={loginStyle.loginSecondFormStyle}>
              <Box display="flex" alignItems="center">
                <Box>
                  <img
                    src={LillyLogo}
                    style={loginStyle.loginLillyLogoStyle}
                    alt="Lilly Employee"
                  />
                </Box>
                <Box>
                  <Typography component="p" sx={loginStyle.loginemployeetext}>
                    {"Employees Click"}
                  </Typography>
                </Box>
                <Box sx={loginStyle.loginformboxlink}>
                  <Box
                    sx={loginStyle.ssoboxClickHereStyle}
                    onClick={() =>
                      (window.location.href = contentProps?.lillyLogin)
                    }>
                    Here
                  </Box>
                </Box>
              </Box>
            </Container>
          </Link>
        )}
      </>
    </Box>
  );
};

export default LoginUI;
